import { useState } from "react";
import classNames from "classnames";
import { isEmpty, map, omit } from "lodash";
import formatDate from "@/helpers/formatDate.ts";
import { useUpdateActivityInCache } from "@/hooks/activitystreams/useUpdateActivityInCache.ts";
import type { Activity, QuestionnaireObject } from "@/@types/activities.ts";
import ParticipationForm from "@/components/questionnaires/participation/ParticipationForm.tsx";
import { useSubmitQuestionnaire } from "@/components/questionnaires/api.ts";
import { translateQuestionnaire } from "@/components/questionnaires/translation.ts";

// From four questions on, the form opens on demand
const COLLAPSE_FROM = 4;

type ActivityQuestionnaireProps = QuestionnaireObject & {
  activityId: string;
  isHit?: boolean;
  readOnly?: boolean;
  questionnaireTranslation?: Record<string, string>;
  // The detail page: always open, refreshes its own query after sending
  standalone?: boolean;
  onSubmitted?: (activity: Activity) => void;
};

function t(key: string, options = {}) {
  return I18n.t(`js.questionnaires.participation.${key}`, options);
}

export default function ActivityQuestionnaire(
  props: ActivityQuestionnaireProps,
) {
  const {
    activityId,
    isHit,
    readOnly,
    standalone,
    onSubmitted,
    can,
    closed,
    my_submission,
  } = props;
  const questionnaire = translateQuestionnaire(
    props,
    props.questionnaireTranslation,
  );
  const count = questionnaire.questions?.length ?? props.question_count ?? 0;
  const description = isHit ? props.content : questionnaire.description;
  const [expanded, setExpanded] = useState(standalone || count < COLLAPSE_FROM);
  const [changing, setChanging] = useState(false);
  // the green note belongs to the moment of sending, not to every later visit
  const [justSaved, setJustSaved] = useState(false);
  const { updateActivity } = useUpdateActivityInCache();
  const { mutateAsync: submit } = useSubmitQuestionnaire(props.id);

  if (props.deleted_questionnaire)
    return <p className="text-muted">{t("deleted")}</p>;

  const answerable = !!can?.submit && !closed && !isHit && !readOnly;
  const submitted = !isEmpty(my_submission);

  async function send(answers) {
    // the server refuses if the questionnaire stopped being anonymous meanwhile
    const activity = await submit({
      answers,
      anonymous: !!questionnaire.anonymous,
    });
    updateActivity(
      activityId,
      (current) =>
        ({
          ...current,
          object: { ...current.object, ...omit(activity.object, ["replies"]) },
        }) as Activity,
    );
    onSubmitted?.(activity);
    setChanging(false);
    setJustSaved(true);
    toastr.success(t("saved"));
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-lg font-semibold m-0 break-words">
        {questionnaire.title}
      </h3>
      {description && (
        <div
          className="markdown-content prose break-words"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      <p className="text-sm text-muted m-0 flex flex-wrap gap-x-3">
        <span>{t("question_count", { count })}</span>
        {questionnaire.anonymous && <span>{t("anonymous")}</span>}
        {closed ? (
          <span className="font-semibold">{t("ended")}</span>
        ) : (
          questionnaire.ends_at && (
            <span>
              {t("ends_at", {
                date: formatDate(new Date(questionnaire.ends_at), "PPPp"),
              })}
            </span>
          )
        )}
      </p>

      {submitted && (closed || (!changing && answerable)) && (
        // green only right after sending; on a later visit it is a plain note
        <div
          className={classNames(
            "flex flex-wrap items-center justify-between gap-2 m-0",
            justSaved ? "alert alert-success" : "text-sm text-muted",
          )}
        >
          <span>{justSaved ? t("saved") : t("participated")}</span>
          <span className="flex flex-wrap items-center gap-2">
            {answerable && (
              <button
                type="button"
                className="btn btn-light btn-sm"
                onClick={() => {
                  setChanging(true);
                  setExpanded(true);
                  setJustSaved(false);
                }}
              >
                {t("change")}
              </button>
            )}
          </span>
        </div>
      )}

      {answerable &&
        (!submitted || changing) &&
        (expanded ? (
          <ParticipationForm
            // answers are kept by position, so other questions need a fresh form
            key={map(questionnaire.questions, "id").join()}
            questionnaire={questionnaire}
            onSubmit={send}
            onCancel={changing ? () => setChanging(false) : undefined}
          />
        ) : (
          <div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setExpanded(true)}
            >
              {t("answer_all", { count })}
            </button>
          </div>
        ))}

      {!answerable && !submitted && !isEmpty(questionnaire.questions) && (
        <ol className="list-decimal pl-6 m-0 text-sm">
          {map(questionnaire.questions, (question) => (
            <li key={question.id}>{question.content}</li>
          ))}
        </ol>
      )}
    </div>
  );
}
